/**
 * ЕДИНОЕ место для цветов, отступов, радиусов и типографики.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. До этого этапа цвета жили в двух местах сразу:
 * объект `palette` в `styles.ts` (inline-стили) и «магические» hex-строки
 * внутри CSS-строки `roomListCss.ts`. Два источника правды означают, что
 * будущая лента неизбежно разъедется по цвету с левой колонкой. Здесь —
 * ОДИН источник, из которого питаются оба механизма.
 *
 * ПРАВИЛО ДЛЯ СЛЕДУЮЩИХ ЭТАПОВ (лента, треды, упоминания):
 *   новый цвет/отступ/размер шрифта сначала появляется ЗДЕСЬ как токен,
 *   и только потом используется. Литерал `#rrggbb` в компоненте или в
 *   CSS-строке считается дефектом ревью, а не мелочью.
 *
 * STEP8 ЭТАП 1 (Антон, 11.09 16:16–16:23): палитра пересобрана на
 * СЛОНОВОЙ КОСТИ как базе всего интерфейса + фирменный красный #E10600
 * из логотипа как акцент. Телеграмно-синяя гамма (#3390ec и т.п.) убрана:
 * это был чужой фирменный цвет, оставшийся с этапа «как в Telegram».
 * Синий в интерфейсе ALG — не наш цвет.
 *
 * УСТРОЙСТВО ТЕМЫ (тёмная тема РЕАЛИЗОВАНА, 2026-09-14):
 *   - все значения живут в объектах `light` и `dark`, имеющих тип
 *     `ThemeColors`;
 *   - компоненты импортируют ТОЛЬКО семантические имена (`color.feedBg`,
 *     `color.bubbleIn`), а не ступени шкалы (`ivory3`) — тёмная тема
 *     подставляет другие значения под те же имена без правки компонентов;
 *   - переключение — CSS-переменные: `color` содержит строки вида
 *     `var(--alg-<ключ>)`, а `installThemeCss` вставляет в `<head>`
 *     `<style>` с `:root { …light… }` и `:root[data-theme="dark"] { …dark… }`.
 *     Смена темы — установка/снятие `data-theme="dark"` на `<html>`.
 *     Никаких перезагрузок, никаких Proxy, никаких event emitter.
 */

/* ------------------------------------------------------------------------
 * СЫРЫЕ СТУПЕНИ. Наружу не экспортируются: компоненты обязаны брать
 * семантические имена из `light`. Если понадобилась новая ступень —
 * сначала думаем, какое у неё семантическое имя.
 * --------------------------------------------------------------------- */

/**
 * Пять ступеней тёплой светлой шкалы (слоновая кость), от самой светлой
 * к самой насыщенной. Все пять — один и тот же тёплый тон (hue ≈ 45–48°,
 * низкая насыщенность), различаются только светлотой: так поверхности
 * отделяются друг от друга БЕЗ чёрных линий и без цветовых конфликтов.
 *
 * Почему не «чистый» ivory #FFFFF0: он слишком жёлтый и на больших площадях
 * читается как «выцветший», а не как «тёплый». Насыщенность приглушена до
 * 30–55% — кость угадывается, желтизна не бьёт в глаза.
 */
const ivory = {
  /** 1. Фон приложения — самая светлая ступень. Почти белый, но тёплый:
      рядом с холодным #ffffff сайта-визитки не сливается, глаз не режет. */
  app: "#FDFCF8",
  /** 2. Лента сообщений — на полтона темнее фона приложения: область
      чтения очерчена, но контраст с белым полем ввода минимален. */
  feed: "#FAF7F0",
  /** 3. Чужой пузырь — заметно темнее ленты (требование Антона 16:21:
      пузырь ОБЯЗАН иметь свой фон, отличный от ленты). Читается без рамки. */
  bubbleIn: "#F3EEE2",
  /** 5. Боковая панель и шапка — самая насыщенная ступень шкалы. Панель
      обязана отличаться от ленты сильнее, чем пузырь: она другая зона
      приложения, а не элемент внутри ленты. */
  panel: "#EFE9DA",
  /**
   * Разделители и границы.
   *
   * ПРАВКА ПО АНТОНУ 16:54 п.1: «рамки более отчётливо». Было #E4DCC8 —
   * «соседняя ступень шкалы» по букве требования 16:21, но на живом экране
   * такая граница почти не видна: разница с фоном ленты ~4%. Антон в 16:26
   * отдельно разрешил серые разделители, поэтому уходим в сторону серого и
   * даём заметный шаг: контраст к ленте ~18% вместо 4%.
   */
  line: "#D9D2C2",
  /**
   * МЯГКАЯ горизонтальная линия — Антон 16:54(2): «рамки шапки чата оставь
   * как есть, поправь вертикальные рамки».
   *
   * Усиление границ касалось ВЕРТИКАЛЬНЫХ швов между колонками. Шапка чата
   * и подвал — горизонтальные линии внутри одной зоны: там прежний светлый
   * тон был верным, и усиливать его Антон не просил. Значение — ровно то,
   * что стояло до правки 16:54.
   */
  lineSoft: "#E4DCC8",
  /** Граница поактивнее (фокус, рамки полей, обрамление панелей): ещё
      ступень вниз, читается на любом из пяти фонов. */
  lineStrong: "#BDB49F",
} as const;

/**
 * Свой пузырь — единственная поверхность, уходящая от нейтральной кости
 * в сторону фирменного красного (решение из постановки: «свой пузырь —
 * тёплый оттенок с лёгким градиентом в сторону красного»). Красный сам по
 * себе фоном пузыря быть не может: на нём нечитаем тёмный текст, а белый
 * текст на красной заливке на больших площадях — резь. Поэтому берём очень светлый
 * тон того же hue (0–8°), и градиентом слегка усиливаем его к низу.
 */
const ownBubble = {
  from: "#FCEBE4",
  to: "#F9DCD0",
} as const;

/**
 * Фирменный красный и его производные.
 *
 * БАЗА — #E10600, фирменный красный живого сайта ALG (--red в
 * alg-site/src/styles/global.css, замерено). Антон 16:20 сказал «цвет из
 * логотипа», в 16:24 после сравнения решил окончательно: «согласен, возьми
 * с сайта».
 *
 * Чистый #FF0000 из файла логотипа (alg-red.svg) в интерфейсе НЕ
 * используется: на больших поверхностях и особенно на слоновой кости он
 * кричит — на сайте его по этой же причине смягчили. #FF0000 остаётся
 * только внутри самого знака, чтобы логотип совпадал с исходником.
 *
 * Наведение и градиентный блик — #FF1A14 (--red-bright сайта).
 */
const brand = {
  /**
   * Акцент интерфейса — РЕШЕНИЕ АНТОНА 16:24: «согласен, возьми с сайта».
   *
   * Это --red живого сайта ALG (alg-site/src/styles/global.css), а НЕ
   * чистый #FF0000 из файла логотипа. Причина зафиксирована: на больших
   * поверхностях и особенно на слоновой кости чистый красный выглядит
   * кричаще, на сайте его по этой же причине смягчили.
   *
   * #FF0000 остаётся ТОЛЬКО внутри самих файлов логотипа, чтобы знак
   * совпадал с исходником. В интерфейсе он не используется.
   */
  red: "#E10600",
  /** Наведение и активное состояние: --red-bright сайта. Кнопка становится
      ярче, а не темнее — на светлой кости это читается как «живая». */
  redHover: "#FF1A14",
  /** Яркая ступень для градиентов (верх кнопки) — тот же --red-bright.
      Градиент red→redBright даёт фирменный блик без выхода из тона. */
  redBright: "#FF1A14",
  /** Мягкий красный фон (своя строка в списке, плашка упоминания, чипсы):
      тот же hue на 6% насыщенности — связь с акцентом угадывается,
      текст на нём читается тёмным. */
  redSoft: "#FDEAE7",
  /** Чуть сильнее redSoft — наведение на мягкий красный фон. */
  redSoftHover: "#FADAD3",
} as const;

/**
 * Тёмно-серый семейство — текст и контролы (Антон: «тёмно-серый остаётся
 * в палитре — для текста, границ, контролов»). Все три ступени в ТЁПЛОМ
 * тоне (hue ≈ 45°): холодный серый (#6b7280 и т.п.) на слоновой кости
 * выглядит синей тенью и спорит с теплом шкалы.
 */
const ink = {
  /** Основной текст. Не чисто чёрный: на тёплом фоне #000 даёт избыточный
      контраст и «звенит»; 2B2A26 даёт контраст 13.9:1 на ivory.app —
      выше WCAG AAA (7:1) с запасом. */
  text: "#2B2A26",
  /** Вторичный текст (время, превью, подписи). Контраст 5.9:1 на ivory.app —
      выше AA (4.5:1) для мелкого текста. */
  dim: "#6E6A5E",
  /** Третичный текст (placeholder, неактивные вкладки). 4.6:1 — на границе
      AA, поэтому только для служебного текста, не для содержимого. */
  muted: "#8A8577",
} as const;

/* ------------------------------------------------------------------------
 * СЕМАНТИЧЕСКАЯ ТЕМА. Имена говорят, ГДЕ цвет используется, а не КАКОЙ
 * он. Тёмная тема = второй объект с теми же ключами.
 * --------------------------------------------------------------------- */

export interface ThemeColors {
  /** Фон всего приложения (самая светлая ступень кости). */
  bg: string;
  /** Фон ленты сообщений. */
  feedBg: string;
  /** Фон чужого пузыря (обязан отличаться от ленты — Антон 16:21). */
  bubbleIn: string;
  /** Фон своего пузыря: начало и конец градиента (тёплый, в сторону красного). */
  bubbleOut: string;
  bubbleOutTo: string;
  /** Фон боковой панели / шапки (самая насыщенная ступень кости). */
  sidebarBg: string;
  /** Рельс спейсов — на полтона темнее панели, как и раньше. */
  railBg: string;
  /** Разделители и рамки: соседняя ступень шкалы, НЕ чёрные линии. */
  border: string;
  /** Горизонтальные линии внутри зоны (шапка чата, подвал) — мягче border. */
  borderSoft: string;
  /** Рамки поактивнее (фокус полей, выделенные элементы). */
  borderStrong: string;

  text: string;
  textDim: string;
  textMuted: string;
  textOnAccent: string;

  /** Фирменный красный: кнопки, активные состояния, фокус. */
  accent: string;
  /** Наведение на акцент. */
  accentHover: string;
  /** Яркая ступень акцента для градиентов. */
  accentBright: string;
  /** Мягкий акцентный фон (активная строка в списке, чипсы, плашки). */
  accentSoft: string;
  accentSoftHover: string;

  /** Подсветка строки под курсором (нейтральная, не акцентная). */
  hover: string;
  /** Подсветка вкладки под курсором. */
  tabHover: string;
  /** Фон счётчика на НЕактивной вкладке. */
  badgeMuted: string;
  /** Текст placeholder в поле поиска/ввода. */
  placeholder: string;

  /** Бейдж роли участника (owner/admin) — приглушённый фиолетовый.
      Оставлен из прошлой темы: к фирменной гамме отношения не имеет,
      а читабельность подтверждена проверкой Антона 10.09. */
  roleBadgeBg: string;
  roleBadgeText: string;

  /** Затемнение под модальным окном / лайтбоксом. rgba: нужна прозрачность. */
  backdrop: string;

  /** СОСТОЯНИЯ (постановка: «подобрать цвета состояний»).
      Все четыре в тёплой гамме, чтобы не спорить с костью:
      холодный синий info и кислотный зелёный на слоновой кости выглядят
      чужеродно. Каждому состоянию — пара: текст/иконка + мягкий фон. */
  /** Успех / подтверждение. Глубокий тёплый зелёный; фон — его светлая ступень. */
  ok: string;
  okBg: string;
  /** Предупреждение. Тёмный янтарь (не жёлтый: жёлтый текст на кости
      нечитаем); фон — светлый янтарь. */
  warn: string;
  warnBg: string;
  warnBorder: string;
  /** Ошибка / разрушительное действие. Отличаем от акцента: accent —
      фирменный #E10600 («наше, действие»), danger — приглушённый кирпичный
      («внимание, опасно»). Один hue на всех означал бы, что кнопка
      «Удалить» неотличима от кнопки «Позвонить». */
  danger: string;
  dangerBg: string;
  /** Выделение (найденное сообщение, подсветка цели перехода по ссылке,
      фон ::selection). Тёплый персиковый: на кости читается как маркер,
      с красным акцентом не конфликтует. */
  highlight: string;
  /** Бейдж упоминания @. Красный — самый срочный сигнал в списке. */
  badgeHighlight: string;

  /* -- УСТАРЕВШИЕ ИМЕНА (совместимость до этапов 2–4) ------------------
     Эти ключи разъезжаются по проекту со времён «как в Telegram».
     Удалить их сейчас — значит править десяток файлов в этапе, где
     разрешены только цвета/шрифты/иконки. Значения маппятся на новую
     семантику; ключи помечены @deprecated и убираются отдельной уборкой. */
  /** @deprecated телеграмный синий, заменён фирменным accent. */
  tgBlue: string;
  /** @deprecated фон/текст служебной плашки секции (приглашения). */
  sectionBg: string;
  sectionText: string;

  /* -- ЦВЕТА ТЕКСТА КОМПОЗЕРА (закрытая палитра из пяти) ------------------
     Раньше зашивались литералами в composerCss.ts и о теме не знали:
     на тёмном фоне красный/зелёный/синий/фиолетовый проваливали WCAG,
     оранжевый проваливал на светлом. Теперь это токены: у каждого цвета
     пара значений (светлое/тёмное), каждое подобрано под контраст ≥ 4.5
     на фонах сообщений своей темы (замеры — scripts/check_contrast.py). */
  /** Текст, окрашенный пользователем в красный. */
  composerRed: string;
  /** Текст, окрашенный пользователем в оранжевый. */
  composerOrange: string;
  /** Текст, окрашенный пользователем в зелёный. */
  composerGreen: string;
  /** Текст, окрашенный пользователем в синий. */
  composerBlue: string;
  /** Текст, окрашенный пользователем в фиолетовый. */
  composerPurple: string;
}

/** Светлая тема — единственная на этом этапе. */
export const light: ThemeColors = {
  bg: ivory.app,
  feedBg: ivory.feed,
  bubbleIn: ivory.bubbleIn,
  bubbleOut: ownBubble.from,
  bubbleOutTo: ownBubble.to,
  sidebarBg: ivory.panel,
  // Рельс темнее панели: промежуточное значение между panel и line,
  // чтобы колонка «спейсы | чаты | лента» читалась тремя ступенями глубины.
  /**
   * Рельс спейсов — САМАЯ тёмная поверхность светлой темы.
   *
   * ПРАВКА ПО АНТОНУ 16:54 п.2: «левая панель где спейсы недостаточно
   * выделена». Было #E9E2D1 — между panel (#EFE9DA) и line, то есть рельс
   * отличался от списка комнат всего на ~2.5% светлоты и визуально с ним
   * слипался. Теперь рельс уходит ощутимо глубже панели: три зоны
   * (рельс -> список -> лента) читаются как три уровня глубины.
   */
  railBg: "#DED6C2",
  border: ivory.line,
  borderSoft: ivory.lineSoft,
  borderStrong: ivory.lineStrong,

  text: ink.text,
  textDim: ink.dim,
  textMuted: ink.muted,
  textOnAccent: "#FFFFFF",

  accent: brand.red,
  accentHover: brand.redHover,
  accentBright: brand.redBright,
  accentSoft: brand.redSoft,
  accentSoftHover: brand.redSoftHover,

  // Нейтральное наведение — panel на полтона темнее: строка под курсором
  // видна, но не кричит акцентом (акцент оставлен ДЕЙСТВИЯМ, а не навигации).
  hover: "#EBE4D3",
  tabHover: "#EBE4D3",
  badgeMuted: "#B4AC97",
  placeholder: ink.muted,

  roleBadgeBg: "#EDE9FB",
  roleBadgeText: "#8B7FD4",

  backdrop: "rgba(43, 42, 38, 0.38)",

  ok: "#2E7D4F",
  okBg: "#E4F2E8",
  warn: "#8F6600",
  warnBg: "#FBF1D8",
  warnBorder: "#EAD49A",
  danger: "#C23A2E",
  dangerBg: "#F9E5E1",
  highlight: "#FFE9C2",
  badgeHighlight: brand.red,

  tgBlue: brand.red,
  sectionBg: "#E4DCC8",
  sectionText: ink.dim,

  /* Палитра композера, светлая тема. Каждое значение подобрано под
     контраст ≥ 4.5 на всех фонах сообщений (bg/feedBg/bubbleIn/bubbleOut/
     bubbleOutTo); замеры — scripts/check_contrast.py. Оранжевый пришлось
     затемнить сильнее прочих: прежний #d97706 на слоновой кости давал 3.10. */
  composerRed: "#B81A14",
  composerOrange: "#A34C00",
  composerGreen: "#147142",
  composerBlue: "#1D66AF",
  composerPurple: "#703DB8",
};

/**
 * Тёмная тема. Те же ключи, что у `light`, но значения пересобраны
 * для тёмного фона. Главное правило: светлые поверхности (bg, feedBg,
 * bubbleIn, sidebarBg, railBg) становятся тёмными, текст — светлым,
 * акцент остаётся фирменным красным, но фоны состояний (okBg, warnBg,
 * dangerBg, highlight) приглушены, чтобы не кричать на тёмном.
 */
export const dark: ThemeColors = {
  bg: "#1C1C1E",
  feedBg: "#232325",
  bubbleIn: "#2C2C2E",
  bubbleOut: "#3A2E2B",
  bubbleOutTo: "#4A3632",
  sidebarBg: "#2C2C2E",
  railBg: "#1C1C1E",
  border: "#3A3A3C",
  borderSoft: "#2C2C2E",
  borderStrong: "#48484A",

  text: "#F2F2F7",
  textDim: "#AEAEB2",
  /* ALG-631: 8E8E93 на bubbleIn/sidebarBg давал 4.27 (< 4.5).
     Осветлён до A8A8AD: 5.89 на bubbleIn, 4.79 на hover — проходит везде. */
  textMuted: "#A8A8AD",
  /* ALG-631: на осветлённом акценте белый текст уже не проходит
     (2.62), поэтому в тёмной теме текст на акценте — тёмный. */
  textOnAccent: "#1C1C1E",

  /* ALG-631: фирменный красный E10600 на тёмных фонах давал 2.63–3.43
     (< 4.5). Осветлённый вариант того же hue (~2°): узнаваемо тот же
     красный, но читаемый: 4.5+ на всех поверхностях, включая hover. */
  accent: "#FA8079",
  accentHover: "#FF8A86",
  accentBright: "#FF8A86",
  accentSoft: "#3A2320",
  accentSoftHover: "#4A2D29",

  hover: "#3A3A3C",
  tabHover: "#3A3A3C",
  /* ALG-631: 636366 на фонах давал 2.33–2.84 (< 3.0). Осветлён до
     757579: ≥ 3.04 ко всем поверхностям, белая цифра на нём — 4.59. */
  badgeMuted: "#757579",
  placeholder: "#A8A8AD",

  roleBadgeBg: "#2D2A3E",
  roleBadgeText: "#9B8FD4",

  backdrop: "rgba(0, 0, 0, 0.55)",

  ok: "#30D158",
  okBg: "#1C2E21",
  warn: "#FFD60A",
  warnBg: "#332D1A",
  warnBorder: "#4A4226",
  /* ALG-631: FF453A на bubbleIn давал 4.09, на hover 3.33. Осветлён до
     FF837A: 5.83 на bubbleIn, 4.74 на hover — проходит. */
  danger: "#FF837A",
  dangerBg: "#3A1C1A",
  highlight: "#3A2E1A",
  badgeHighlight: "#FA8079",

  tgBlue: "#FA8079",
  sectionBg: "#2C2C2E",
  sectionText: "#AEAEB2",

  /* Палитра композера, тёмная тема. Светлые ступени тех же оттенков:
     ≥ 4.5 на всех фонах сообщений (замеры — scripts/check_contrast.py). */
  composerRed: "#FA8079",
  composerOrange: "#FF9E3D",
  composerGreen: "#47D18C",
  composerBlue: "#71B2F4",
  composerPurple: "#C2A0E9",
};

/* ------------------------------------------------------------------------
 * CSS-ПЕРЕМЕННЫЕ. Тема переключается сменой одного атрибута на <html> —
 * мгновенно, без перерисовки, без перезагрузки.
 *
 * КАК ЭТО РАБОТАЕТ:
 * 1. `buildCssVars` из объекта темы делает строку `--alg-<ключ>: <значение>;`
 *    для каждого ключа.
 * 2. `installThemeCss` вставляет в <head> тег <style> с двумя наборами:
 *    `:root { …светлая… }` и `:root[data-theme="dark"] { …тёмная… }`.
 * 3. `color` ниже — объект того же типа `ThemeColors`, но каждое значение
 *    — строка `var(--alg-<ключ>)`. Все 32 импорта продолжают работать
 *    без правок: браузер сам подставляет нужный цвет.
 * 4. `applyTheme` ставит/снимает `data-theme="dark"` на <html>.
 *    Режим `system` читает `matchMedia("(prefers-color-scheme: dark)")`
 *    и подписывается на изменения — сменилась тема macOS, клиент поехал
 *    следом сам, без перезагрузки.
 * 5. Выбор хранится в localStorage, ключ `alg.theme`. По умолчанию `system`.
 * --------------------------------------------------------------------- */

/** Собрать строку CSS-переменных из объекта темы. */
function buildCssVars(theme: ThemeColors): string {
  return Object.entries(theme)
    .map(([key, value]) => `--alg-${key}: ${value};`)
    .join(" ");
}

/** Вставить в <head> тег <style> с обоими наборами переменных. */
export function installThemeCss(): void {
  if (document.querySelector("style[data-alg-theme]")) return;
  const el = document.createElement("style");
  el.setAttribute("data-alg-theme", "1");
  el.textContent =
    `:root { ${buildCssVars(light)} --alg-font-scale: 1; }\n` +
    `:root[data-theme="dark"] { ${buildCssVars(dark)} }\n` +
    /* Scaling the root also covers inline pixel font sizes. Width/height are
       compensated so the scaled root still fills the viewport. */
    `#root { zoom: var(--alg-font-scale, 1); width: calc(100% / var(--alg-font-scale, 1)); height: calc(100% / var(--alg-font-scale, 1)); }`;
  document.head.appendChild(el);
}

/** Текущий режим темы из localStorage. По умолчанию "system". */
export function readThemeMode(): "system" | "light" | "dark" {
  try {
    const v = localStorage.getItem("alg.theme");
    if (v === "light" || v === "dark") return v;
  } catch {
    /* localStorage может быть недоступен (приватный режим) */
  }
  return "system";
}

/** Записать режим темы в localStorage. */
export function writeThemeMode(mode: "system" | "light" | "dark"): void {
  try {
    localStorage.setItem("alg.theme", mode);
  } catch {
    /* ignore */
  }
}

export type FontSizeMode = "small" | "default" | "large";
const FONT_SIZE_KEY = "alg.fontSize";

export function readFontSizeMode(): FontSizeMode {
  try {
    const value = localStorage.getItem(FONT_SIZE_KEY);
    if (value === "small" || value === "default" || value === "large") return value;
  } catch {
    /* localStorage may be unavailable */
  }
  return "default";
}

export function applyFontSize(mode: FontSizeMode): void {
  const scale = mode === "small" ? 0.875 : mode === "large" ? 1.125 : 1;
  try {
    localStorage.setItem(FONT_SIZE_KEY, mode);
  } catch {
    /* ignore */
  }
  document.documentElement.style.setProperty("--alg-font-scale", String(scale));
}

/** Активная подписка на смену системной темы (для отмены). */
let systemThemeCleanup: (() => void) | null = null;

/**
 * Применить тему. mode: "system" | "light" | "dark".
 * При "system" читает matchMedia и подписывается на изменения.
 * При "light"/"dark" ставит/снимает data-theme="dark" на <html>.
 */
export function applyTheme(mode: "system" | "light" | "dark"): void {
  writeThemeMode(mode);
  // Отменяем предыдущую подписку, если была.
  systemThemeCleanup?.();
  systemThemeCleanup = null;

  const root = document.documentElement;
  if (mode === "dark") {
    root.setAttribute("data-theme", "dark");
    return;
  }
  if (mode === "light") {
    root.removeAttribute("data-theme");
    return;
  }
  // system
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  const sync = () => {
    if (mql.matches) {
      root.setAttribute("data-theme", "dark");
    } else {
      root.removeAttribute("data-theme");
    }
  };
  sync();
  mql.addEventListener?.("change", sync);
  systemThemeCleanup = () => mql.removeEventListener?.("change", sync);
}

/** Активная тема. Каждое значение — строка var(--alg-<ключ>). */
// ГОТЧА (14.09): Object.fromEntries возвращает { [k: string]: string } — TS не видит
// в нём конкретные ключи ThemeColors и отказывает в прямом приведении
// (TS2352, «missing bg, feedBg, … and 34 more»). Ключи берутся из `light`,
// т.е. набор гарантированно полный — проходим через unknown.
export const color: ThemeColors = Object.fromEntries(
  Object.keys(light).map((key) => [key, `var(--alg-${key})`]),
) as unknown as ThemeColors;

/* ------------------------------------------------------------------------
 * СОВМЕСТИМОСТЬ: устаревшие имена живут прямо в `ThemeColors` выше
 * (tgBlue/sectionBg/sectionText), чтобы существующие вызовы не ломались.
 * --------------------------------------------------------------------- */

/**
 * Палитра заглушек аватара. Индекс берётся детерминированно из room_id —
 * см. `colorOf`. Из id, а не из имени: имя меняется, цвет меняться не должен.
 *
 * Гамма ПРИГЛУШЕНА относительно старой (телеграмные чистые цвета): на
 * слоновой кости чистые #e17076 и т.п. кричали. Оттенки затемнены и
 * слегка растеплены (~12–15% темнее), узнаваемость по цвету сохранена.
 */
export const avatarPalette = [
  "#C65D63", "#62A34E", "#C9A95C", "#548CB8",
  "#8B7BC9", "#CF6392", "#55A3A5",
] as const;

/** Детерминированный цвет заглушки по идентификатору комнаты/спейса. */
export function colorOf(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return avatarPalette[Math.abs(hash) % avatarPalette.length];
}

/** Отступы. Шаг 4px — вся сетка кратна ему. */
export const space = {
  xs: 2,
  sm: 4,
  md: 8,
  lg: 12,
  xl: 16,
} as const;

export const radius = {
  sm: 6,
  md: 8,
  /** Пузыри сообщений — этап 2 задаст им форму; радиус уже здесь. */
  bubble: 12,
  pill: 18,
  circle: "50%",
} as const;

/**
 * Типографика. Размеры в px, чтобы совпадали с замерами в прогоне.
 *
 * СТЕК (Антон: «наиболее читабельные»): Inter первым — он спроектирован
 * именно для экранного чтения интерфейсов (крупный x-height, открытые
 * апертуры, различимые I/l/1) и уже используется на сайте ALG
 * (alg-site global.css) — один шрифт на продукты. Если Inter не подгружен,
 * дальше системный стек без перелома раскладки (метрики близкие).
 * Моноширинный — для технических строк (id, коды): tabular figures.
 *
 * БАЗОВЫЙ РАЗМЕР поднят 14→15 (Антон: «немного больше нынешнего»).
 * 15 — наименьший шаг, который читается как «стало крупнее», а не как
 * «что-то изменилось, не пойму что»: +1px к 14 даёт +7% площади знака.
 * Остальные ступени подняты соразмерно, шкала сохранена.
 */
export const font = {
  family:
    "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  mono: "'SF Mono', 'Cascadia Mono', Consolas, 'Roboto Mono', monospace",
  /**
   * ТЕКСТ СООБЩЕНИЯ — КЕГЛЬ iMessage, 17 px.
   *
   * Антон 25.09 21:24: «можешь сделать шрифт как в iMessage?» и 21:26
   * «Сделай везде, на айфоне критично». У iMessage тело сообщения —
   * текстовый стиль `.body`, то есть 17 pt. На iOS соответствует
   * `ALGTheme.messageBody` (тем же ходом вернут с `.subheadline` на `.body`).
   *
   * ПОЧЕМУ ОТДЕЛЬНЫЙ ТОКЕН, А НЕ ПОДНЯТЫЙ `body`. `font.body`
   * сидит не только на сообщении: на кнопках, полях диалогов,
   * пунктах меню, монограмме аватара и на ПРЕВЬЮ в списке комнат
   * (`s.roomPreview`). Поднять сам `body` до 17 значило бы раздуть всю
   * обвязку и сделать превью (17) КРУПНЕЕ имени комнаты (`roomName`
   * = 16) — иерархия списка встала бы вверх ногами. Просили кегль
   * СООБЩЕНИЯ, а не редизайн кнопок и навигации. iOS устроен так же:
   * там тоже ОТДЕЛЬНЫЙ `messageBody`, а кегли списка живут своими
   * числами и не следуют за телом.
   *
   * Соседи по пузырю остались на месте и продолжают быть мельче тела:
   * имя автора и цитата реплая — `small` (14), время в пузыре — `tiny`
   * (12.5), время в шапке группы — `micro` (11.5). До правки разрыв
   * тело→имя был 1 px, теперь 3 — иерархия стала только чётче.
   */
  messageBody: 17,
  /**
   * БАЗОВЫЙ кегль интерфейса — кнопки, поля, диалоги, пункты меню,
   * превью в списке комнат (был 14).
   *
   * НЕ РАВЕН ТЕКСТУ СООБЩЕНИЯ с 25.09 — см. `messageBody` выше.
   * До этой даты число было одно на двоих, и поэтому часть старых
   * комментариев рядом говорит «15 = размер ленты» — больше не так.
   */
  body: 15,
  /**
   * Имя комнаты в списке — ЧУТЬ КРУПНЕЕ ОСТАЛЬНОГО СПИСКА (превью 15).
   *
   * ПРАВКА ПО АНТОНУ 16:54 п.4: «размер шрифта в списке комнат должен быть
   * чуть крупнее текста сообщений». Раньше и то и другое было font.body,
   * то есть список и лента звучали одинаково. Список комнат — навигация,
   * её сканируют взглядом, поэтому она должна читаться первой.
   * Шаг 1px намеренно маленький: 17+ уже выглядит как заголовок.
   *
   * С 25.09 СРАВНЕНИЕ С ЛЕНТОЙ БОЛЬШЕ НЕ ДЕЙСТВУЕТ: тело сообщения
   * стало 17 (`messageBody`, кегль iMessage по просьбе Антона 21:26), то есть
   * крупнее имени комнаты. Так и задумано и так же на iOS после той же
   * правки: тело и список стоят на РАЗНЫХ экранах и рядом не видны;
   * значима внутренняя иерархия списка (имя 16 > превью 15 > время 12.5),
   * а она сохранена без изменений. Не поднимать 16 «чтобы было больше
   * ленты» — это уже редизайн навигации, его не просили.
   */
  roomName: 16,
  /** Превью, вкладки, вспомогательный текст (был 13). */
  small: 14,
  /** Время, счётчики (был 12). */
  tiny: 12.5,
  /** Заголовки секций, подвал (был 11). */
  micro: 11.5,
  /** Межстрочный интервал для чтения: на 15px базе 1.5 читается свободнее
      прежнего 1.45, плотность ленты почти не страдает.

      МНОЖИТЕЛЬ, А НЕ ПИКСЕЛИ — и именно поэтому подъём тела
      сообщения 15 → 17 (25.09) не потребовал правки здесь: интервал
      поднялся сам, 22.5px → 25.5px. Задай он абсолютом под 15 — текст
      слипся бы. Не переводить в px. */
  lineBody: 1.5,
  weightNormal: 400,
  // 700, а не 600: рядом с обычным 400 разница должна читаться с одного
  // взгляда, иначе «жирным» пометка не работает (проверка Антона 10.09).
  weightBold: 700,
  weightHeavy: 700,
} as const;

/**
 * Размеры левой колонки. Вынесены сюда, потому что от них зависят
 * одновременно и inline-стили, и CSS-строка, и проверки живого прогона
 * (шаг «высота строки 60px» сверяется именно с этим числом).
 */
export const size = {
  /** Ширина колонки со списком (без рельса). */
  sidebarWidth: 320,
  /** Ширина вертикального рельса спейсов. */
  railWidth: 56,
  /** Высота строки чата. ФИКСИРОВАННАЯ — см. комментарий в RoomList.tsx. */
  rowHeight: 60,
  /** Диаметр аватара в строке чата. */
  avatar: 44,
  /** Диаметр иконки спейса в рельсе. */
  railIcon: 40,
  /** Максимальная ширина вкладки: длинное имя спейса обрезаем, а не тянем ряд. */
  tabMaxWidth: 180,
} as const;
